/**
 * CSV Files API - CRUD operacije za CSV datoteke
 */

import { httpClient } from '../client/httpClient';
import { supabase } from '../client/supabaseClient';
import { FileData } from '../types/apiTypes';
import { toSnakeCase, appendToFormData } from '../utils/fieldMapper';
import { logError } from '../utils/errorHandler';

export const csvFilesApi = {
  /**
   * Dohvati sve CSV datoteke za određenu sesiju
   * Returns raw API response data for backward compatibility
   */
  getCSVFiles: async (sessionId: string, type?: 'input' | 'output'): Promise<unknown[]> => {
    try {
      const endpoint = type
        ? `/api/training/csv-files/${sessionId}?type=${type}`
        : `/api/training/csv-files/${sessionId}`;

      const response = await httpClient.get<unknown[]>(endpoint);

      if (!response.success) {
        throw new Error(response.error || 'Failed to fetch CSV files');
      }

      return response.data || [];
    } catch (error) {
      logError('csvFilesApi.getCSVFiles', error);
      throw error;
    }
  },

  /**
   * Stvori novi CSV fajl
   */
  createCSVFile: async (
    sessionId: string,
    fileData: FileData,
    file?: File
  ): Promise<unknown> => {
    try {
      if (file) {
        // Upload sa fajlom - koristi FormData
        const formData = new FormData();
        formData.append('sessionId', sessionId);
        formData.append('file', file);

        // Dodaj file metadata
        appendToFormData(formData, fileData as unknown as Record<string, unknown>);

        const response = await httpClient.postFormData<unknown>(
          '/api/training/csv-files',
          formData
        );

        if (!response.success) {
          throw new Error(response.error || 'Failed to create CSV file');
        }

        return response.data;
      } else {
        // Samo metadata bez fajla
        const response = await httpClient.post<unknown>('/api/training/csv-files', {
          sessionId,
          fileData,
        });

        if (!response.success) {
          throw new Error(response.error || 'Failed to create CSV file');
        }

        return response.data;
      }
    } catch (error) {
      logError('csvFilesApi.createCSVFile', error);
      throw error;
    }
  },

  /**
   * Ažuriraj postojeći CSV fajl
   */
  updateCSVFile: async (fileId: string, fileData: FileData): Promise<unknown> => {
    try {
      // Convert camelCase to snake_case
      const snakeCaseData = toSnakeCase(fileData as unknown as Record<string, unknown>);

      const response = await httpClient.put<unknown>(
        `/api/training/csv-files/${fileId}`,
        snakeCaseData
      );

      if (!response.success) {
        throw new Error(response.error || 'Failed to update CSV file');
      }

      return response.data;
    } catch (error) {
      logError('csvFilesApi.updateCSVFile', error);
      throw error;
    }
  },

  /**
   * Obriši CSV datoteku
   */
  deleteCSVFile: async (fileId: string): Promise<boolean> => {
    try {
      const response = await httpClient.delete(`/api/training/csv-files/${fileId}`);

      if (!response.success) {
        throw new Error(response.error || 'Failed to delete CSV file');
      }

      return true;
    } catch (error) {
      logError('csvFilesApi.deleteCSVFile', error);
      throw error;
    }
  },

  // Legacy support methods
  getCSVFile: async (): Promise<never> => {
    throw new Error('getCSVFile not implemented - use getCSVFiles instead');
  },

  uploadFileToStorage: async (): Promise<never> => {
    throw new Error('uploadFileToStorage not needed - use createCSVFile');
  },

  /**
   * Download a CSV file from Supabase Storage
   */
  downloadCSVFile: async (
    storagePath: string,
    type: 'input' | 'output',
    fileName: string
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      const bucket = type === 'output' ? 'aus-csv-files' : 'csv-files';
      const { data, error } = await supabase.storage.from(bucket).download(storagePath);

      if (error) {
        logError('csvFilesApi.downloadCSVFile', error);
        return { success: false, error: error.message };
      }

      // Trigger browser download
      const url = URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      return { success: true };
    } catch (error) {
      logError('csvFilesApi.downloadCSVFile', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Download failed',
      };
    }
  },
};
